import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import { Envelope, Phone, GeoAlt, Clock } from "react-bootstrap-icons";
import { Seo } from "../components/Seo";
import { generateStaticPageSEO } from "../lib/seo/metaGenerators";

export function Contact() {
  const contactInfo = [
    { icon: Envelope, title: "Email", content: "support@tecofood.com", link: "mailto:support@tecofood.com" },
    { icon: Phone, title: "Phone", content: "+1 (555) 123-4567", link: "tel:+15551234567" },
    { icon: GeoAlt, title: "Address", content: "123 Food Street, Cuisine City, FC 12345", link: "https://maps.google.com" },
    { icon: Clock, title: "Hours", content: "24/7 Customer Support", link: null }
  ];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } };
  const itemVariants = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.42 } } };

  return (
    <>
    <Seo {...generateStaticPageSEO({
      title: "Contact Us",
      description: "Get in touch with the Teco team for support, partnership inquiries, or general questions about food delivery in Kenya.",
      path: "/contact",
    })} />
    <Container className="contact-shell" as="section" aria-label="Contact">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="contact-inner">

        <motion.header variants={itemVariants} className="contact-head">
          <h1 className="contact-title">Contact Us</h1>
          <p className="contact-lead micro">Have questions? We're here to help — reach us via email, phone, or the contact form below.</p>
        </motion.header>

        <motion.div variants={itemVariants} className="info-grid">
          {contactInfo.map((info, i) => {
            const Icon = info.icon;
            return (
              <article className="info-card" key={i} role="article" aria-label={info.title}>
                <div className="info-inner">
                  <div className="info-icon"><Icon className="icon-line" /></div>
                  <div className="info-body">
                    <div className="info-title">{info.title}</div>
                    {info.link ? (
                      <a href={info.link} className="info-link">{info.content}</a>
                    ) : (
                      <div className="info-text">{info.content}</div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </motion.div>

        <motion.div variants={itemVariants} className="form-wrap">
          <div className="form-card">
            <h2 className="form-title">Send us a message</h2>
            <p className="form-sub micro">We typically respond within 24 hours.</p>
            <Form>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Control placeholder="Name" className="input clean" aria-label="Name" />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Control placeholder="Email" className="input clean" aria-label="Email" />
                </Col>
                <Col xs={12}>
                  <Form.Control placeholder="Subject" className="input clean" aria-label="Subject" />
                </Col>
                <Col xs={12}>
                  <Form.Control as="textarea" rows={4} placeholder="Your message..." className="input clean" aria-label="Message" />
                </Col>
                <Col xs={12}>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="submit-btn" aria-label="Send message">Send Message</motion.button>
                </Col>
              </Row>
            </Form>
          </div>
        </motion.div>

      </motion.div>

      <style>{`
        :root{--maxw:980px;--gap:12px;--radius:12px;--muted:0,0,0;--accent:26,115,232}
        .contact-shell{padding:20px 12px}
        .contact-inner{max-width:var(--maxw);margin:0 auto;display:flex;flex-direction:column;gap:18px}

        .contact-head{text-align:center}
        .contact-title{margin:0;font-size:22px;font-weight:800;color:rgba(var(--muted),0.92)}
        .contact-lead{margin:0;color:rgba(0,0,0,0.66);font-size:14px;max-width:760px;margin-left:auto;margin-right:auto}

        /* Info grid */
        .info-grid{display:grid;grid-template-columns:1fr;gap:12px;align-items:stretch}
        @media(min-width:600px){.info-grid{grid-template-columns:repeat(2,1fr);gap:14px}}
        @media(min-width:1024px){.info-grid{grid-template-columns:repeat(4,1fr);gap:16px}}

        .info-card{display:flex;min-height:100%}
        .info-inner{display:flex;flex:1;gap:12px;align-items:flex-start;padding:12px;border-radius:calc(var(--radius) + 2px);background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.06);transition:transform 160ms cubic-bezier(.2,.9,.2,1),box-shadow 160ms cubic-bezier(.2,.9,.2,1)}

        .info-icon{flex:0 0 40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;background:rgba(var(--accent),0.06)}
        .icon-line{width:20px;height:20px;stroke:rgba(0,0,0,0.72);stroke-width:1.2}

        .info-title{font-size:13px;font-weight:700;margin-bottom:4px}
        .info-text,.info-link{font-size:14px;color:rgba(0,0,0,0.86);text-decoration:none}
        .info-link{color:rgba(0,0,0,0.66)}
        .info-link:hover{color:rgba(var(--accent),1)}

        /* Form card */
        .form-card{padding:14px;border-radius:14px;background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.05)}
        .form-title{margin:0;font-size:18px;font-weight:700}
        .form-sub{margin:0;color:rgba(0,0,0,0.46);margin-bottom:10px}

        .input.clean{background:rgba(245,245,247,0.9);border:1px solid rgba(0,0,0,0.04);padding:12px;border-radius:10px}
        .input.clean:focus{outline:none;box-shadow:none;border-color:rgba(var(--accent),1)}

        .submit-btn{display:inline-flex;width:100%;align-items:center;justify-content:center;padding:12px;border-radius:999px;border:1px solid rgba(0,0,0,0.06);background:linear-gradient(rgba(255,255,255,0.98),rgba(255,255,255,0.98));color:var(--bs-primary);font-weight:800;cursor:pointer}
        .submit-btn:hover{transform:translateY(-2px);box-shadow:0 10px 30px rgba(11,22,40,0.04)}

        @media (prefers-reduced-motion: reduce){.info-inner,.submit-btn{transition:none!important}}
        .contact-inner,.info-inner{transform:translateZ(0);backface-visibility:hidden}

        @media(max-width:420px){.contact-title{font-size:20px}.info-icon{flex:0 0 36px;height:36px}.input.clean{padding:10px}}
      `}</style>
    </Container>
    </>
  );
}
